import { QUESTS_API_URL } from "../config/questsApi";
import { API_URL } from "../config/api";
import { isQuestJournalFilterActive, prepareQuestJournalFiltersForApply } from "../constants/questJournalFilters";
import { pickTitleWordCounts } from "./titleWordCounts";

const QUEST_POLL_INTERVAL_MS = 15_000;

export { QUEST_POLL_INTERVAL_MS };

function assertQuestsApiUrl() {
  if (!QUESTS_API_URL) {
    throw new Error(
      "URL des quêtes non configurée (REACT_APP_QUESTS_API_URL manquant)."
    );
  }
}

export async function fetchTodayQuests() {
  assertQuestsApiUrl();

  const res = await fetch(`${QUESTS_API_URL}/today`);
  const json = await res.json().catch(() => ({}));

  if (res.status === 404) {
    return {
      found: false,
      activeDate: json.activeDate || null,
      nextResetAt: json.nextResetAt || null,
      message: json.message || "Aucune quête pour la période en cours.",
    };
  }

  if (!res.ok) {
    throw new Error(json.message || "Impossible de charger les quêtes du jour.");
  }

  return {
    found: true,
    ...json,
  };
}

export async function fetchQuestJournal(filters = null) {
  assertQuestsApiUrl();

  const hasFilters = isQuestJournalFilterActive(filters);
  const preparedFilters = hasFilters ? prepareQuestJournalFiltersForApply(filters) : null;

  const res = hasFilters
    ? await fetch(`${QUESTS_API_URL}/journal/filter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filters: preparedFilters }),
      })
    : await fetch(`${QUESTS_API_URL}/journal`);

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible de charger le journal de quête.");
  }

  return json;
}

export async function fetchQuestJournalFilterOptions() {
  assertQuestsApiUrl();

  const res = await fetch(`${QUESTS_API_URL}/journal/filter-options`);
  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible de charger les options de filtre.");
  }

  return json;
}

export async function fetchQuestUnreadCount() {
  if (!QUESTS_API_URL) return 0;

  try {
    const data = await fetchTodayQuests();
    return data.found ? data.unreadCompletedCount || 0 : 0;
  } catch {
    return 0;
  }
}

export async function markQuestsRead() {
  assertQuestsApiUrl();

  const res = await fetch(`${QUESTS_API_URL}/mark-read`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible de marquer les quêtes comme consultées.");
  }

  return json;
}

export async function confirmReadingObjective(objectiveId) {
  assertQuestsApiUrl();

  const res = await fetch(`${QUESTS_API_URL}/reading/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ objectiveId }),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible de valider l'objectif de lecture.");
  }

  return json;
}

export async function markQuestHistoryRecorded() {
  assertQuestsApiUrl();

  const res = await fetch(`${QUESTS_API_URL}/history-recorded`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible de confirmer l'historique de quête.");
  }

  return json;
}

export async function fetchRadiantDayTitles() {
  assertQuestsApiUrl();

  const params = new URLSearchParams();
  try {
    const progressRes = await fetch(`${API_URL}/mastery/progress`);
    const progress = await progressRes.json().catch(() => ({}));
    const wordCounts = pickTitleWordCounts(progress);
    if (progressRes.ok && wordCounts) {
      if (wordCounts.capturedWordCount != null) {
        params.set("capturedWordCount", String(wordCounts.capturedWordCount));
      }
      if (wordCounts.masteredWordCount != null) {
        params.set("masteredWordCount", String(wordCounts.masteredWordCount));
      }
    }
  } catch (err) {
    console.warn("Quêtes — synchro mots du registre :", err.message);
  }

  const query = params.toString();
  const res = await fetch(
    `${QUESTS_API_URL}/radiant-day-titles${query ? `?${query}` : ""}`
  );
  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible de charger les titres.");
  }

  return json;
}

export async function fetchRadiantPlayerProfile() {
  assertQuestsApiUrl();

  const res = await fetch(`${QUESTS_API_URL}/radiant-player-profile`);
  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible de charger le profil.");
  }

  return json;
}

export async function uploadRadiantPlayerAvatar(dataUrl) {
  assertQuestsApiUrl();

  const res = await fetch(`${QUESTS_API_URL}/radiant-player-profile/avatar`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dataUrl }),
  });
  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible d'enregistrer la photo.");
  }

  return json;
}

export async function saveRadiantDisplayedTitleId(titleId) {
  assertQuestsApiUrl();

  const res = await fetch(`${QUESTS_API_URL}/radiant-player-profile/displayed-grade`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ titleId: titleId ?? null }),
  });
  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible d'enregistrer le grade affiché.");
  }

  return json;
}

export async function fetchOwnedRelicFileUrl(relicId) {
  if (!QUESTS_API_URL || !relicId) return "";

  try {
    const res = await fetch(
      `${QUESTS_API_URL}/relics/${encodeURIComponent(relicId)}/file`
    );
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.url) return "";
    return String(json.url);
  } catch {
    return "";
  }
}

export async function fetchQuestInventory() {
  if (!QUESTS_API_URL) {
    return { ownedRelicIds: [] };
  }

  try {
    const res = await fetch(`${QUESTS_API_URL}/inventory`);
    const json = await res.json().catch(() => ({}));

    if (!res.ok) {
      return { ownedRelicIds: [] };
    }

    return {
      ownedRelicIds: Array.isArray(json.ownedRelicIds) ? json.ownedRelicIds : [],
      relics: json.relics && typeof json.relics === "object" ? json.relics : {},
    };
  } catch {
    return { ownedRelicIds: [], relics: {} };
  }
}

export async function claimQuestReward(date) {
  assertQuestsApiUrl();

  const res = await fetch(`${QUESTS_API_URL}/reward/claim`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ date }),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible d'ouvrir le coffre de quête.");
  }

  return json;
}

// Crédite les pierres/potions d'une quête dans l'inventaire principal.
// Les reliques restent gérées par le backend des quêtes. Le crédit est
// idempotent côté serveur (dédupliqué par date), donc l'appel peut être
// rejoué sans risque de double-crédit.
export async function creditQuestRewardToInventory(date, grants, claimToken) {
  if (!date || !Array.isArray(grants) || grants.length === 0) {
    return null;
  }

  const creditable = grants.filter(
    (grant) => grant && (grant.kind === "gem" || grant.kind === "potion" || grant.kind === "key")
  );

  if (creditable.length === 0) {
    return null;
  }

  const res = await fetch(`${API_URL}/quest-rewards/credit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ date, grants: creditable, claimToken: claimToken || null }),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible de créditer les lots de quête.");
  }

  return json;
}

// --- Liqueur du Métamorphe ----------------------------------------------------

export async function fetchMetamorphCandidates() {
  assertQuestsApiUrl();

  const res = await fetch(`${QUESTS_API_URL}/metamorph/candidates`);
  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible de préparer la Liqueur du Métamorphe.");
  }

  return json;
}

export async function applyMetamorph(objectiveId, categoryId) {
  assertQuestsApiUrl();

  const res = await fetch(`${QUESTS_API_URL}/metamorph/apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ objectiveId, categoryId }),
  });
  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    const error = new Error(json.message || "Impossible de remplacer cet objectif.");
    error.status = res.status;
    error.code = json.code || null;
    throw error;
  }

  return json;
}

// Débite une Liqueur du Métamorphe dans l'inventaire principal après un
// remplacement enregistré par l'API des quêtes. Idempotent côté serveur
// (dédupliqué par jeton), donc rejouable sans risque de double débit.
export async function debitMetamorphPotion(date, debitToken) {
  if (!date || !debitToken) return null;

  const res = await fetch(`${API_URL}/potions/metamorph-debit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ date, debitToken }),
  });
  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible de débiter la Liqueur du Métamorphe.");
  }

  return json;
}

export async function fetchDemiLuneStatus() {
  if (!QUESTS_API_URL) return null;
  const res = await fetch(`${QUESTS_API_URL}/demi-lune`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.message || "Impossible de lire le Philtre de la demi-lune.");
  }
  return json;
}

export async function activateDemiLunePotion() {
  assertQuestsApiUrl();
  const res = await fetch(`${QUESTS_API_URL}/demi-lune/activate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(
      json.message || "Impossible d'utiliser le Philtre de la demi-lune."
    );
    error.status = res.status;
    error.code = json.code || null;
    throw error;
  }
  return json;
}

export async function debitDemiLunePotion(date, debitToken) {
  if (!date || !debitToken) return null;

  const res = await fetch(`${API_URL}/potions/demi-lune-debit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ date, debitToken }),
  });
  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible de débiter le Philtre de la demi-lune.");
  }

  return json;
}

export async function fetchPotionCounts() {
  const res = await fetch(`${API_URL}/potions/counts`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.message || "Impossible de lire le stock de potions.");
  }
  return json.counts || {};
}

export function formatQuestStatusLabel(status) {
  switch (status) {
    case "completed":
      return "Accompli";
    case "failed":
      return "Non accompli";
    case "pending":
    default:
      return "En cours";
  }
}

export async function fetchQuestEmailPreference() {
  assertQuestsApiUrl();

  const res = await fetch(`${QUESTS_API_URL}/email-preference`);
  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible de charger la préférence d'e-mail.");
  }

  return {
    enabled: json.enabled !== false,
    canChange: json.canChange !== false,
    nextChangeAt: json.nextChangeAt || null,
  };
}

export async function saveQuestEmailPreference(enabled) {
  assertQuestsApiUrl();

  const res = await fetch(`${QUESTS_API_URL}/email-preference`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled: Boolean(enabled) }),
  });
  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    const error = new Error(
      json.message || "Impossible d'enregistrer la préférence d'e-mail."
    );
    error.status = res.status;
    error.payload = json;
    throw error;
  }

  return {
    enabled: json.enabled !== false,
    canChange: json.canChange !== false,
    nextChangeAt: json.nextChangeAt || null,
  };
}

export function formatNextResetLabel(nextResetAt) {
  if (!nextResetAt) return null;

  const target = new Date(nextResetAt);
  if (Number.isNaN(target.getTime())) return null;

  return target.toLocaleString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatQuestCountdown(nextResetAt, now = new Date()) {
  if (!nextResetAt) return null;

  const targetMs = new Date(nextResetAt).getTime();
  if (!Number.isFinite(targetMs)) return null;

  const diffMs = Math.max(0, targetMs - now.getTime());
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
}
